class UpsellPopup extends HTMLElement {
  connectedCallback() {
    this._boundHostClick = (e) => {
      if (e.target === this) {
        document.dispatchEvent(new CustomEvent('cart:close'));
      } else {
        e.stopPropagation();
      }
    };

    this._init();
    this._listenCart();
    this._listenResize();

    // Prevent drawer from closing when clicking inside the popup
    this.addEventListener('click', this._boundHostClick);
  }

  disconnectedCallback() {
    if (this._onCartUpdated) {
      document.removeEventListener('cart:updated', this._onCartUpdated);
    }

    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }

    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }

    if (this._trigger && this._onTriggerClick) {
      this._trigger.removeEventListener('click', this._onTriggerClick);
    }

    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
      this._drawerObserver = null;
    }

    if (this._resizeTimer) {
      window.clearTimeout(this._resizeTimer);
    }

    this.removeEventListener('click', this._boundHostClick);
  }

  _init() {
    this._dropdown = this.querySelector('.custom-dropdown');
    if (!this._dropdown) return;

    this._trigger = this._dropdown.querySelector('.custom-dropdown__trigger');
    this._selected = this._dropdown.querySelector('.custom-dropdown__selected');
    this._options = Array.from(this._dropdown.querySelectorAll('.custom-dropdown__option'));

    this._bindDropdown();
    this._bindProductBlocks();
    this._observeDrawer();
    this._bindUpsellActions();
  }

  _listenCart() {
    if (this._cartListenerAttached) return;
    this._cartListenerAttached = true;

    this._onCartUpdated = () => {
      this._refresh();
    };

    document.addEventListener('cart:updated', this._onCartUpdated);
  }

  _listenResize() {
    if (this._resizeListenerAttached) return;
    this._resizeListenerAttached = true;

    this._onResize = () => {
      if (this._resizeTimer) {
        window.clearTimeout(this._resizeTimer);
      }

      this._resizeTimer = window.setTimeout(() => {
        this.querySelectorAll('.upsell-popup__slider.flickity-enabled').forEach((slider) => {
          if (slider.flickityInstance && slider.offsetParent !== null) {
            slider.flickityInstance.resize();
            this._equalizeSlideHeights(slider);
          }
        });
      }, 100);
    };

    window.addEventListener('resize', this._onResize);
  }

  async _refresh() {
    // Save current state
    const opts = this._options || [];
    const previousKeys = new Set(opts.map((o) => o.dataset.value?.trim()));
    const currentItem = opts.find((o) => o.dataset.selected === 'true')?.dataset.value?.trim();
    const visibleBlock = Array.from(this.querySelectorAll('.upsell-popup__product-upsells')).find((b) => b.style.display !== 'none');
    const currentTab = visibleBlock?.querySelector('.upsell-popup__tab.is-active')?.dataset.tab;

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart?view=ajax');
      const text = await response.text();
      const div = document.createElement('div');
      div.innerHTML = text;
      const newPopup = div.querySelector('upsell-popup');

      if (newPopup) {
        this.className = newPopup.className;
        this.innerHTML = newPopup.innerHTML;
        this._init();

        // Prefer newly added item; fall back to restoring previous selection
        const addedKey = (this._options || []).map((o) => o.dataset.value?.trim()).find((k) => k && !previousKeys.has(k));

        if (addedKey) {
          const opt = (this._options || []).find((o) => o.dataset.value?.trim() === addedKey);
          if (opt) {
            this._options.forEach((o) => delete o.dataset.selected);
            opt.dataset.selected = 'true';
            this._selected.innerHTML = opt.innerHTML;
            this._showBlock(addedKey);
          }
        } else if (currentItem) {
          const opt = (this._options || []).find((o) => o.dataset.value.trim() === currentItem);
          if (opt) {
            this._options.forEach((o) => delete o.dataset.selected);
            opt.dataset.selected = 'true';
            this._selected.innerHTML = opt.innerHTML;
            this._showBlock(currentItem, false);

            if (currentTab) {
              const activeBlock = Array.from(this.querySelectorAll('.upsell-popup__product-upsells')).find(
                (b) => b.dataset.forItem.trim() === currentItem,
              );
              if (activeBlock) {
                const tab = activeBlock.querySelector(`.upsell-popup__tab[data-tab="${currentTab}"]`);
                if (tab) tab.click();
              }
            }
          }
        }

        // Always init slider for the visible block after refresh
        setTimeout(() => {
          const visibleBlock = Array.from(this.querySelectorAll('.upsell-popup__product-upsells')).find((b) => b.style.display !== 'none');
          if (visibleBlock) this._initSliderInBlock(visibleBlock);
        }, 150);
      }
    } catch (e) {
      console.error('[upsell] Refresh failed:', e);
    }
  }

  // ─── Dropdown ────────────────────────────────────────────────────────────────

  _bindDropdown() {
    if (this._dropdown.dataset.eventsAttached) return;
    this._dropdown.dataset.eventsAttached = 'true';

    // Toggle open/close
    this._onTriggerClick = (e) => {
      e.stopPropagation();
      this._dropdown.dataset.open = this._dropdown.dataset.open === 'true' ? 'false' : 'true';
    };
    this._trigger.addEventListener('click', this._onTriggerClick);

    // Close on outside click
    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }

    this._onDocumentClick = () => {
      this._dropdown.dataset.open = 'false';
    };
    document.addEventListener('click', this._onDocumentClick);

    // Select an option → switch product block
    this._options.forEach((opt) => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        this._options.forEach((o) => delete o.dataset.selected);
        opt.dataset.selected = 'true';
        this._selected.innerHTML = opt.innerHTML;
        this._dropdown.dataset.open = 'false';
        this._showBlock(opt.dataset.value.trim());
      });
    });

    // Show the initially selected block
    const initial = this._options.find((o) => o.dataset.selected === 'true') || this._options[0];
    if (initial) this._showBlock(initial.dataset.value.trim(), false);
  }

  // ─── Product blocks ───────────────────────────────────────────────────────────

  _bindProductBlocks() {
    this.querySelectorAll('.upsell-popup__product-upsells').forEach((block) => {
      if (block.dataset.eventsAttached) return;
      block.dataset.eventsAttached = 'true';

      const tabs = block.querySelectorAll('.upsell-popup__tab');
      const contents = block.querySelectorAll('.upsell-popup__tab-content');

      const switchTab = (target, shouldFocus = false) => {
        let activeTab = null;

        tabs.forEach((t) => {
          const isActive = t.dataset.tab === target;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          t.setAttribute('tabindex', isActive ? '0' : '-1');

          if (isActive) {
            activeTab = t;
          }
        });

        contents.forEach((c) => {
          const isActive = c.dataset.tabContent === target;
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        this._initSliderInBlock(block);

        if (shouldFocus && activeTab) {
          activeTab.focus();
        }
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          switchTab(tab.dataset.tab);
        });

        tab.addEventListener('keydown', (event) => {
          const tabsArray = Array.from(tabs);
          const currentIndex = tabsArray.indexOf(tab);
          if (currentIndex === -1) return;

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % tabsArray.length;
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + tabsArray.length) % tabsArray.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabsArray.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          const nextTab = tabsArray[nextIndex];
          if (!nextTab) return;

          switchTab(nextTab.dataset.tab, true);
        });
      });
    });
  }

  _showBlock(key, initSlider = true) {
    const blocks = Array.from(this.querySelectorAll('.upsell-popup__product-upsells'));
    if (!blocks.length) return;

    let target = blocks.find((b) => b.dataset.forItem.trim() === key) || blocks[0];

    blocks.forEach((b) => {
      b.style.display = b === target ? 'block' : 'none';
    });

    const activeTab = target.querySelector('.upsell-popup__tab.is-active');
    if (!activeTab) {
      const tabs = Array.from(target.querySelectorAll('.upsell-popup__tab'));
      const contents = Array.from(target.querySelectorAll('.upsell-popup__tab-content'));
      if (tabs.length) {
        tabs.forEach((t) => t.classList.remove('is-active'));
        contents.forEach((c) => c.classList.remove('is-active'));
        tabs[0].classList.add('is-active');
        const first = contents.find((c) => c.dataset.tabContent === tabs[0].dataset.tab);
        if (first) first.classList.add('is-active');
      }
    }

    if (initSlider) {
      if (this._isDrawerOpen()) {
        setTimeout(() => this._initSliderInBlock(target), 100);
      } else {
        this._initSliderInBlock(target);
      }
    }

    const tabs = Array.from(target.querySelectorAll('.upsell-popup__tab'));
    const contents = Array.from(target.querySelectorAll('.upsell-popup__tab-content'));
    const currentTabEl = tabs.find((tab) => tab.classList.contains('is-active'));

    tabs.forEach((tab) => {
      const isActive = tab === currentTabEl;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    contents.forEach((content) => {
      const isActive = content.classList.contains('is-active');
      content.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  _isDrawerOpen() {
    const drawer = document.getElementById('CartDrawer');
    return !!(drawer && drawer.classList.contains('drawer--is-open'));
  }

  // ─── Slider ───────────────────────────────────────────────────────────────────

  _initSliderInBlock(block) {
    const activeContent = block.querySelector('.upsell-popup__tab-content.is-active');
    if (!activeContent) return;

    const slider = activeContent.querySelector('.upsell-popup__slider');
    if (!slider) return;

    if (slider.classList.contains('flickity-enabled')) {
      if (slider.flickityInstance) {
        slider.flickityInstance.reloadCells();
        slider.flickityInstance.resize();
        this._equalizeSlideHeights(slider);
      }
      return;
    }

    const slides = slider.querySelectorAll('.upsell-popup__slide');
    if (!slides.length) return;

    const flkty = new window.Flickity(slider, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: true,
      groupCells: true,
      dragThreshold: 10,
      adaptiveHeight: true,
      imagesLoaded: true,
    });

    slider.flickityInstance = flkty;
    slider.classList.remove('flickity-viewport-off');
    this._equalizeSlideHeights(slider);
    setTimeout(() => this._equalizeSlideHeights(slider), 400);
  }

  _equalizeSlideHeights(slider) {
    const slides = Array.from(slider.querySelectorAll('.upsell-popup__slide'));
    if (!slides.length) return;

    slides.forEach((s) => (s.style.minHeight = ''));

    const maxHeight = Math.max(...slides.map((s) => s.offsetHeight));
    if (maxHeight <= 0) return;

    slides.forEach((s) => (s.style.minHeight = `${maxHeight}px`));

    if (slider.flickityInstance) {
      slider.flickityInstance.resize();
    }
  }

  // ─── Upsell Actions (Qty & Add to Cart) ──────────────────────────────────────

  _bindUpsellActions() {
    if (this._upsellActionsAttached) return;
    this._upsellActionsAttached = true;

    this.addEventListener('click', (e) => {
      const target = e.target;
      const minusBtn = target.closest('.qty-minus');
      const plusBtn = target.closest('.qty-plus');

      if (minusBtn || plusBtn) {
        const qtyWrapper = target.closest('.upsell-card__quantity');
        const input = qtyWrapper.querySelector('.qty-input');
        const parsedValue = Number.parseInt(input.value, 10);
        let val = Number.isNaN(parsedValue) ? 1 : parsedValue;

        if (minusBtn) {
          val = Math.max(1, val - 1);
        } else {
          val++;
        }
        input.value = val;
        return;
      }

      const addBtn = target.closest('.js-upsell-add');
      if (addBtn) {
        this._handleAddToCart(addBtn);
      }
    });
  }

  _handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const cardInfo = button.closest('.upsell-card__info');
    const quantity = cardInfo.querySelector('.qty-input').value;
    const labelDefault = button.dataset.labelDefault || button.textContent;
    const labelAdding = button.dataset.labelAdding || labelDefault;
    const labelAdded = button.dataset.labelAdded || labelDefault;
    const labelError = button.dataset.labelError || labelDefault;

    button.disabled = true;
    button.textContent = labelAdding;

    const formData = {
      items: [
        {
          id: variantId,
          quantity: Number.parseInt(quantity, 10),
        },
      ],
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        button.textContent = labelAdded;

        document.dispatchEvent(new CustomEvent('cart:build'));
        fetch(window.Shopify.routes.root + 'cart.js')
          .then((res) => res.json())
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          });

        setTimeout(() => {
          button.disabled = false;
          button.textContent = labelDefault;
        }, 2000);
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        button.disabled = false;
        button.textContent = labelError;
        setTimeout(() => {
          button.textContent = labelDefault;
        }, 2000);
      });
  }

  // ─── Re-init when CartDrawer opens (MutationObserver on class changes) ────────

  _observeDrawer() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    if (this._drawerObserver) this._drawerObserver.disconnect();

    const togglePopupClass = () => {
      const isOpen = drawer.classList.contains('drawer--is-open');
      document.documentElement.classList.toggle('js-upsell-popup-open', isOpen);

      if (isOpen) {
        setTimeout(() => {
          this._bindProductBlocks();
          const visibleBlock = Array.from(this.querySelectorAll('.upsell-popup__product-upsells')).find(
            (b) => b.style.display !== 'none'
          );
          if (visibleBlock) this._initSliderInBlock(visibleBlock);
        }, 100);
      }
    };

    this._drawerObserver = new MutationObserver(togglePopupClass);

    this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // Initial check
    togglePopupClass();
  }
}

if (!customElements.get('upsell-popup')) {
  customElements.define('upsell-popup', UpsellPopup);
}
